import moment from 'moment'

import { lineFormatService, FORMAT_TYPES } from './line-format.service'
import { ScaleType } from '../../charts/line/line-chart.service'
import { reportLayoutService } from '../report-layout.service'

class LinePropService {
  /**
   * Handles all the mapping from the block config into the props the line chart needs downstream
   */
  map (props, propOverrides) {
    // Apply prop overrides
    // This is a legacy feature we don't expose for new charts - it is only used in 2 reports
    // We need to remove this by breaking charts that use this override feature into two charts
    // This will avoid the need for this added complexity
    props = reportLayoutService.applyPropOverrides(props, propOverrides)

    let mapped = this.mapToLineChartFormat(props)

    // Defaults
    mapped.dataDefinition = mapped.dataDefinition ?? { emptyValue: 'NA', xField: 'x', yField: 'n' }

    if (this.isTimeSeries(mapped.scaleTypeX)) {
      mapped = this.mapDateProps(mapped)
    }

    const formatters = this.mapAxesFormatters(mapped)
    mapped.formatterX = formatters.x
    mapped.formatterY = formatters.y

    return mapped
  }

  /**
   * Determine whether a particular scale is time series or not
   */
  isTimeSeries (scaleType) {
    return scaleType === ScaleType.Utc
  }

  /**
   * Retrieve the config (legend order/labels) for the specified strata value
   */
  getStrataConfig (strataDefinition, strataValue) {
    let strata = strataDefinition?.options?.find(
      (option) => option.value === strataValue
    ) ?? { value: strataValue }
    strata = { ...strata }
    const legend = strata.legend ?? []

    strata.legendLabelMap = legend.reduce((result, legend) => {
      result[legend.value] = legend.label
      return result
    }, {})

    return strata
  }

  /**
   * Merge the bounds passed in with the bounds calculated
   */
  mergeBounds (boundsProp = {}, boundsCalculated = {}) {
    const bounds = { ...boundsProp }
    if (bounds) {
      bounds.min = bounds.min ?? boundsCalculated?.min
      bounds.max = bounds.max ?? boundsCalculated?.max
    }
    return bounds
  }

  /**
   * Maps from the block config to the format that the line chart expects
   *
   * If we migrate all existing configs we can remove this step
   */
  mapToLineChartFormat (props) {
    // Mapping from [min, max] to { min, max }
    // The array form caused complexity when we wanted to specify a max with no min
    const convertDomainToBounds = (domain = []) => {
      if (domain.length === 2) {
        return { min: domain[0], max: domain[1] }
      }
      if (domain.length === 1) {
        return { min: domain[0] }
      }
    }

    // Pull out the legacy block config properties that won't be passed along to the line chart
    const { isTimeSeries, xValueDomain, yValueDomain, axisLabels, xTickValues, ...mapped } = props

    mapped.scaleTypeX = isTimeSeries ? ScaleType.Utc : ScaleType.Linear
    mapped.boundsX = convertDomainToBounds(xValueDomain)
    mapped.boundsY = convertDomainToBounds(yValueDomain)
    mapped.labelX = axisLabels?.x
    mapped.labelY = axisLabels?.y
    mapped.tickValuesX = xTickValues

    return mapped
  }

  /**
   * Block properties all come in as strings/objects/arrays, but some need to be converted to actual Date objects
   * so they can be handled in scales & formatters downstream.
   */
  mapDateProps (props) {
    if (Array.isArray(props.tickValuesX)) {
      props.tickValuesX = props.tickValuesX.map(this.convertToDate)
    }
    if (Array.isArray(props.tickValuesY)) {
      props.tickValuesY = props.tickValuesY.map(this.convertToDate)
    }
    if (Array.isArray(props.markers)) {
      props.markers = props.markers.map(({ x, label }) => ({ x: this.convertToDate(x), label }))
    }

    const convertBoundsValue = (bounds, prop) => {
      if (bounds?.[prop] !== undefined) {
        bounds[prop] = this.convertToDate(bounds[prop])
      }
    }
    convertBoundsValue(props.boundsX, 'min')
    convertBoundsValue(props.boundsX, 'max')
    convertBoundsValue(props.boundsY, 'min')
    convertBoundsValue(props.boundsY, 'max')

    return props
  }

  convertToDate (value) {
    return moment.utc(value).toDate()
  }

  mapAxesFormatters ({ scaleTypeX, timeFormat, xFormat, yFormat }) {
    xFormat = (this.isTimeSeries(scaleTypeX))
      ? FORMAT_TYPES.DATE
      : (xFormat ?? FORMAT_TYPES.NUMBER)

    // Use short versions on y axis to save space
    yFormat = (yFormat === FORMAT_TYPES.NUMBER) ? FORMAT_TYPES.NUMBER_SHORT : yFormat
    yFormat = (yFormat === FORMAT_TYPES.PERCENT) ? FORMAT_TYPES.PERCENT_SHORT : yFormat

    return {
      x: lineFormatService.getFormatter({ formatType: xFormat, timeFormat }),
      y: lineFormatService.getFormatter({ formatType: yFormat })
    }
  }
}

export const linePropService = new LinePropService()
